import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { selectCurrentToken, selectCustomerId } from '../auth/authSlice'; // Import selectors

// Define a service using a base URL and expected endpoints
export const apiSlice = createApi({
  reducerPath: 'api',
  baseQuery: fetchBaseQuery({
    baseUrl: 'https://app.hirebroker.co/api', // Use the full API URL for production
    prepareHeaders: (headers, { getState }) => {
      console.log('API Base URL:', 'https://app.hirebroker.co/api'); // Debugging line
      let token = null;
      if (typeof getState === 'function') {
        // Get token from Redux store
        token = selectCurrentToken(getState());
      } else {
        console.error("getState is not a function in prepareHeaders:", getState);
      }
      
      if (token) {
        headers.set('Authorization', `Bearer ${token}`);
      }
      return headers;
    },
  }),
  tagTypes: ['Property', 'User'],
  
  endpoints: (builder) => ({

    getProperties: builder.query({
      query: (params) => ({
        url: '/get_property', // Ensure this uses the relative path for proxy
        params, // This will include pagination parameters
      }),
      transformResponse: (response) => ({
        data: response.data || [],
        total_count: response.total || 0, // Use 'total' from the provided API response
      }),
      // For pagination, we want each page to be cached separately

      providesTags: (result) =>
        result?.data
          ? [
              // Map over the returned properties to create individual tags
              ...result.data.map(({ id }) => ({ type: 'Property', id })),
              // Also provide a general list tag for invalidation
              { type: 'Property', id: 'LIST' },
            ]
          : [{ type: 'Property', id: 'LIST' }],
    }),

    getHostels: builder.query({
      query: (params) => ({
        url: '/get_hostels', // Ensure this uses the relative path for proxy
        params,
      }),
      transformResponse: (response) => ({
        data: response.data || [],
        total_count: response.total_count || 0,
      }),
      providesTags: (result) =>
        result?.data
          ? [
              ...result.data.map(({ id }) => ({ type: 'Property', id })),
              { type: 'Property', id: 'HOSTEL_LIST' },
            ]
          : [{ type: 'Property', id: 'HOSTEL_LIST' }],
    }),

    getHotels: builder.query({
      query: (params) => ({
        url: '/get_hotels', // Ensure this uses the relative path for proxy
        params,
      }),
      transformResponse: (response) => ({
        data: response.data || [],
        total_count: response.total_count || 0,
      }),
      providesTags: (result) =>
        result?.data
          ? [
              ...result.data.map(({ id }) => ({ type: 'Property', id })),
              { type: 'Property', id: 'HOTEL_LIST' },
            ]
          : [{ type: 'Property', id: 'HOTEL_LIST' }],
    }),


    // Add endpoint to get a single property by its slug_id
    getProperty: builder.query({
      query: (slug_id) => ({
        url: '/get_property', // Ensure this uses the relative path for proxy
        params: { slug_id }
      }),
      // The API returns an array with one item, so we extract it.
      transformResponse: (response) => (response.data && response.data.length > 0) ? response.data[0] : null,
      providesTags: (result, error, slug_id) => [{ type: 'Property', id: slug_id }],
    }),
    
    // Add endpoint to get slider images
    getSlider: builder.query({
      query: () => '/get_slider', // Ensure this uses the relative path for proxy
      transformResponse: (response) => response.data || [],
    }),
    // Add endpoint to get categories
    getCategories: builder.query({
      query: () => '/get_categories', // Ensure this uses the relative path for proxy
      transformResponse: (response) => response.data || [],
    }),

    // User signup/login
    userSignup: builder.mutation({
      query: (userData) => ({
        url: '/user_signup', // This endpoint handles both signup and login
        method: 'POST',
        body: userData,
      }),
      transformResponse: (response) => {
        // Assuming the response structure is { error: false, message: "...", token: "...", data: { id: ... } }
        if (response.error === false && response.token && response.data?.id) {
          return {
            user: response.data,
            token: response.token,
            customerId: response.data.id,
          };
        }
        return response; // Return original response if structure is unexpected
      },
    }),

    // ✅ WORKING: get-user-data (different response format)
    getUserData: builder.query({ // Reverted to query based on server error
      query: () => '/get-user-data', // Reverted to GET request
      transformResponse: (response, meta) => {
        // This endpoint uses {"error":false,"data":{...}} format
        // Handle 404 gracefully
        if (meta?.response?.status === 404) {
          return null; // Return null for 404
        }
        return response.data || response;
      },
      providesTags: [{ type: 'User', id: 'CURRENT_USER' }], // Provide tag for caching
    }),

    // Add endpoint to update user profile
    updateUserProfile: builder.mutation({
      query: (userData) => ({
        url: '/update-user-profile', // Placeholder endpoint for updating user profile
        method: 'PUT', // Or POST, depending on API design
        body: userData,
      }),
      invalidatesTags: [{ type: 'User', id: 'CURRENT_USER' }], // Invalidate user data after update
    }),

    // User dashboard endpoints
    // ✅ WORKING: getUserSavedProperties
    getUserSavedProperties: builder.query({
      query: (_, { getState } = {}) => {
        const safeGetState = typeof getState === 'function' ? getState : () => ({});
        const customerId = selectCustomerId(safeGetState()) || localStorage.getItem('customerId');
        console.log('Fetching Saved Properties for Customer ID:', customerId); // Debugging line
        return {
          url: '/getUserSavedProperties', // Ensure this uses the relative path for proxy
          params: { customer_id: customerId }
        };
      },
      transformResponse: (response) => response?.data || [],
    }),

    // ✅ WORKING: getUserSearches (with Bearer + customer_id)
    getUserSearches: builder.query({
      query: (_, { getState } = {}) => {
        const safeGetState = typeof getState === 'function' ? getState : () => ({});
        const customerId = selectCustomerId(safeGetState()) || localStorage.getItem('customerId');
        return {
          url: '/getUserSearches', // Ensure this uses the relative path for proxy
          params: { customer_id: customerId }
        };
      },
      transformResponse: (response) => response?.data || [],
    }),

    // ✅ WORKING: getUserAlerts
    getUserAlerts: builder.query({
      query: (_, { getState } = {}) => {
        const safeGetState = typeof getState === 'function' ? getState : () => ({});
        const customerId = selectCustomerId(safeGetState()) || localStorage.getItem('customerId');
        return {
          url: '/getUserAlerts', // Ensure this uses the relative path for proxy
          params: { customer_id: customerId }
        };
      },
      transformResponse: (response, meta) => {
        // Handle 404 gracefully
        if (meta?.response?.status === 404) {
          return []; // Return empty array for 404
        }
        return response?.data || [];
      },
    }),

    // ✅ WORKING: getUserTours
    getUserTours: builder.query({
      query: (_, { getState } = {}) => {
        const safeGetState = typeof getState === 'function' ? getState : () => ({});
        const customerId = selectCustomerId(safeGetState()) || localStorage.getItem('customerId');
        return {
          url: '/getUserTours', // Ensure this uses the relative path for proxy
          params: { customer_id: customerId }
        };
      },
      transformResponse: (response) => response?.data || [],
    }),

    // ✅ WORKING: getUserActivities
    getUserActivities: builder.query({
      query: (_, { getState } = {}) => {
        const safeGetState = typeof getState === 'function' ? getState : () => ({});
        const customerId = selectCustomerId(safeGetState()) || localStorage.getItem('customerId');
        return {
          url: '/getUserActivities', // Ensure this uses the relative path for proxy
          params: { customer_id: customerId }
        };
      },
      transformResponse: (response) => response?.data || [],
    }),

    // Add endpoint to get user's recently viewed properties
    getUserRecentlyViewed: builder.query({
      query: (_, { getState } = {}) => {
        const safeGetState = typeof getState === 'function' ? getState : () => ({});
        const customerId = selectCustomerId(safeGetState()) || localStorage.getItem('customerId');
        return {
          url: '/getUserRecentlyViewed', // Ensure this uses the relative path for proxy
          params: { customer_id: customerId }
        };
      },
      transformResponse: (response) => response?.data || [],
    }),

    // ✅ WORKING: get_favourite_property (different response format)
    getUserFavouriteProperty: builder.query({
      query: (_, { getState } = {}) => {
        const safeGetState = typeof getState === 'function' ? getState : () => ({});
        const customerId = selectCustomerId(safeGetState()) || localStorage.getItem('customerId');
        return {
          url: '/get_favourite_property', // Ensure this uses the relative path for proxy
          params: { customer_id: customerId }
        };
      },
      transformResponse: (response, meta) => {
        // This endpoint uses {"error":false,"message":"...","data": [...]}
        // Handle 404 gracefully
        if (meta?.response?.status === 404) {
          return []; // Return empty array for 404
        }
        return response?.data || [];
      },
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ id }) => ({ type: 'Property', id })),
              { type: 'Property', id: 'FAVORITE_LIST' },
            ]
          : [{ type: 'Property', id: 'FAVORITE_LIST' }],
    }),

    // Add favorite property
    addFavorite: builder.mutation({
      query: (propertyId, { getState } = {}) => {
        const safeGetState = typeof getState === 'function' ? getState : () => ({});
        const customerId = selectCustomerId(safeGetState()) || localStorage.getItem('customerId');
        return {
          url: '/add_favourite', // Ensure this uses the relative path for proxy
          method: 'POST',
          body: { // This is line 223
            customer_id: customerId,
            type: 1,
            property_id: propertyId
          },
        };
      },
      invalidatesTags: [{ type: 'Property', id: 'FAVORITE_LIST' }],
    }),

    // Delete favorite property
    deleteFavorite: builder.mutation({
      query: (propertyId, { getState } = {}) => {
        const safeGetState = typeof getState === 'function' ? getState : () => ({});
        const customerId = selectCustomerId(safeGetState()) || localStorage.getItem('customerId');
        return {
          url: '/delete_favourite', // Ensure this uses the relative path for proxy
          method: 'POST',
          body: { // This is line 241
            customer_id: customerId,
            type: 0,
            property_id: propertyId
          },
        };
      },
      invalidatesTags: [{ type: 'Property', id: 'FAVORITE_LIST' }],
    }),

    // Add endpoint to get count by cities and categories
    getCountByCitiesCategories: builder.query({
      query: () => '/get_count_by_cities_categoris', // Ensure this uses the relative path for proxy
      transformResponse: (response) => response.data || [],
    }),

    // Add endpoint to get facilities
    getFacilities: builder.query({
      query: () => '/get_facilities', // Ensure this uses the relative path for proxy
      transformResponse: (response) => response.data || [],
    }),

    // New Endpoints
    getArticles: builder.query({
      query: () => '/get_articles',
      transformResponse: (response) => response.data || [],
    }),
    getReportReasons: builder.query({
      query: () => '/get_report_reasons',
      transformResponse: (response) => response.data || [],
    }),
    getSeoSettings: builder.query({
      query: () => '/get_seo_settings',
      transformResponse: (response) => response.data || [],
    }),
    getSystemSettings: builder.query({
      query: () => ({
        url: '/get_system_settings',
        method: 'POST', // The user feedback indicates this is a POST request
      }),
      transformResponse: (response) => response.data || {},
    }),
    handlePaymentResponse: builder.query({ // Assuming this is a GET for now
      query: () => '/payment-response',
      transformResponse: (response) => response.data || response,
    }),
    getUserPersonalisedInterest: builder.query({
      query: () => '/personalised-fields',
      transformResponse: (response) => response.data || [],
    }),
    getInterestedUsers: builder.query({
      query: (params) => ({
        url: '/get_interested_users',
        params,
      }),
      transformResponse: (response) => response.data || [],
    }),
    getPropertyInquiry: builder.query({
      query: (params) => ({
        url: '/get_property_inquiry',
        params,
      }),
      transformResponse: (response) => response.data || [],
    }),
    getNotificationList: builder.query({
      query: () => '/get_notification_list',
      transformResponse: (response) => response.data || [],
    }),
    getPaymentDetails: builder.query({
      query: () => '/get_payment_details',
      transformResponse: (response) => response.data || [],
    }),
    getPaymentSettings: builder.query({
      query: () => '/get_payment_settings',
      transformResponse: (response) => response.data || [],
    }),
    getLimits: builder.query({
      query: () => '/get_limits',
      transformResponse: (response) => response.data || [],
    }),
    getMessages: builder.query({
      query: (params) => ({
        url: '/get_messages',
        params,
      }),
      transformResponse: (response) => response.data || [],
    }),
    getChats: builder.query({
      query: () => '/get_chats',
      transformResponse: (response) => response.data || [],
    }),
    getUserRecommendation: builder.query({
      query: () => '/get_user_recommendation',
      transformResponse: (response) => response.data || [],
    }),
    paypal: builder.query({ // Assuming this is a GET for now
      query: () => '/paypal',
      transformResponse: (response) => response.data || response,
    }),
    getAgentsDetails: builder.query({
      query: () => '/get_agents_details',
      transformResponse: (response) => response.data || [],
    }),
  }),
});

// Export auto-generated hooks for use in your components
export const {
  useGetPropertiesQuery,
  useGetPropertyQuery,
  useGetSliderQuery,
  useGetCategoriesQuery,
  useUserSignupMutation, // This mutation now handles both signup and login
  useGetUserDataQuery,
  useGetUserSavedPropertiesQuery,
  useGetUserSearchesQuery,
  useGetUserAlertsQuery,
  useGetUserToursQuery,
  useGetUserActivitiesQuery,
  useGetUserFavouritePropertyQuery,
  useGetUserRecentlyViewedQuery,
  useAddFavoriteMutation,
  useDeleteFavoriteMutation,
  useGetHostelsQuery,
  useGetHotelsQuery,
  useGetCountByCitiesCategoriesQuery,
  useGetFacilitiesQuery,
  useUpdateUserProfileMutation, // Added new mutation export
  useGetArticlesQuery,
  useGetReportReasonsQuery,
  useGetSeoSettingsQuery,
  useGetSystemSettingsQuery,
  useHandlePaymentResponseQuery,
  useGetUserPersonalisedInterestQuery,
  useGetInterestedUsersQuery,
  useGetPropertyInquiryQuery,
  useGetNotificationListQuery,
  useGetPaymentDetailsQuery,
  useGetPaymentSettingsQuery,
  useGetLimitsQuery,
  useGetMessagesQuery,
  useGetChatsQuery,
  useGetUserRecommendationQuery,
  usePaypalQuery,
  useGetAgentsDetailsQuery
} = apiSlice;
